<template>
  <v-container fluid id="settlements-by-payee">
    <v-data-table
    :items="mappedPayees"
    :headers="headers"
    :expanded="expanded"
    item-key="payeeId"
    show-expand
    single-expand
    @click:row="rowClicked"
    style="cursor: pointer;"
    dense
    fixed-header
    height="max(256px, calc(100vh - 485px))"
    :items-per-page="getDataTableDefaultItemsPerPage($vuetify.breakpoint)"
    :footer-props="{
      'items-per-page-options': dataTableItemsPerPage
    }">
      <template #item.amount="{item}">
        {{formatMoney(item.amount)}}
      </template>
      <template #item.netVolume="{item}">
        {{mbfStringFromBf(item.netVolume)}}
      </template>
      <template #item.grossVolume="{item}">
        {{mbfStringFromBf(item.grossVolume)}}
      </template>
      <template #item.rate="{item}">
        <Icon
        v-if="item.inlineRates?.length > 1"
        icon="mdi-dots-horizontal"
        iconColor="black"
        :tooltipText="$t('multipleRatesForPayee')"
        @icon-clicked="rateVerificationClicked(item)"
        elementId="slrv"
        />
        <span v-else>
          {{ item.inlineRates[0] ? `${formatMoney(item.inlineRates[0])}/t` : $t('notAvailable') }}
        </span>
      </template>
      <template #item.status="{item}">
        <v-tooltip bottom :color="getSettlementStatus(item.payeeId).color">
          <template v-slot:activator="{ on }">
            <v-chip
            v-on="on"
            small
            style="cursor: pointer;"
            @click.stop="toggleSettlementStatus(item)"
            :class="getSettlementStatus(item.payeeId).color"
            id="settlement-status-chip">
              {{getSettlementStatus(item.payeeId).text}}
            </v-chip>
          </template>
          <span class="subtitle-1 white--text">{{getSettlementStatus(item.payeeId).tooltipText}}</span>
        </v-tooltip>
      </template>
      <template #expanded-item="{item}">
        <td :colspan="headers.length + 1" id="sbp-row-expanded">
          <SettlementsByPayeeTable :payee="item" @refresh-settlement="refreshSettlement" :isByproduct="isByproduct"/>
        </td>
      </template>
    </v-data-table>
    <Dialog
    :stateId="dialogId"
    @close="closeDialogs"
    >
      <RateVerificationDialog
      v-if="rateVerification"
      :payeeId="rateVerificationPayeeId"
      @close="closeRateVerification()"
      @refresh-batch="refreshSettlement()"
      :contractMode="contractMode"/>
    </Dialog>
  </v-container>
</template>

<script>
import { formatMoney, tonStringFromPounds, numberWithCommas, mbfStringFromBf } from '@/utils/NumericMutations'
import { journalEntrySettlementByPayeeHeaders } from '@/headers/Cycles.js'
import { SettlementColumnContexts } from '@/components/settlements/SettlementKeys.js'
import { mapActions, mapGetters } from 'vuex'
import { PayPeriodStatus, ContractMode, SettlementVerificationType, SettlementVerificationStatus, SettlementInvalidatedReason, UserClaims } from '@/utils/Enumerations.js'
import { uniqueDialogId, dataTableItemsPerPage, getDataTableDefaultItemsPerPage } from '@/utils/componentHelpers'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'

const TONS_DECIMALS = 3

export default {
  name: 'SettlementsByPayee',

  components: {
    SettlementsByPayeeTable: () => import('@/components/settlements/accruals-module/settlements-by-payee/SettlementsByPayeeTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    RateVerificationDialog: () => import('@/components/settlements/rate-verification/RateVerificationDialog.vue'),
    Dialog: () => import('@/components/Dialog.vue')
  },

  props: {
    settlementsObject: {
      type: Object,
      required: true
    },
    isByproduct: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    dataTableItemsPerPage,
    expanded: [],
    rateVerification: false,
    rateVerificationPayeeId: undefined,
    currentPayee: null,
    dialogId: uniqueDialogId('je-settlements-by-payee'),
    verificationMap: {},
    payees: []
  }),

  computed: {
    ...mapGetters('settlements', ['dateConfiguration', 'settlementPayeeStatuses', 'settlementContractStatuses', 'currentSubModuleKey']),
    mappedPayees () {
      if (this.settlementsObject === undefined) { return [] }
      const payeesModified = this.settlementsObject.payees
      payeesModified.forEach(e => {
        Object.assign(e, {
          loads: this.getNumberOfLoads(e),
          netTons: this.getPayeeNetTonsTotal(e),
          grossTons: this.getPayeeGrossTonsTotal(e),
          netVolume: this.getPayeeVolumeTotal(e, 'netVolume'),
          grossVolume: this.getPayeeVolumeTotal(e, 'grossVolume'),
          amount: this.getPayeeTotalUnformatted(e),
          status: this.getSettlementStatus(e.payeeId).text,
          rate: e.inlineRates
        })
      })
      return payeesModified
    },

    hasVolumeData () {
      return this.mappedPayees.some(p => p.netVolume > 0 || p.grossVolume > 0)
    },

    headers () {
      const settlementContext = this.isByproduct ? undefined : SettlementColumnContexts.AccrualsByPayee
      return journalEntrySettlementByPayeeHeaders(!this.isByproduct && this.hasVolumeData, settlementContext).getColumns()
    },

    isExportBatch () {
      return this.dateConfiguration?.exportBatch?.exportBatchId > 0
    },

    exportBatchId () {
      return this.dateConfiguration.exportBatch.exportBatchId
    },

    exportBatchExportedUnixMs () {
      return (this.dateConfiguration.exportBatch)
        ? Date.parse(`${this.dateConfiguration.exportBatch.exportDate}Z`)
        : undefined
    },

    payPeriodId () {
      return this.dateConfiguration.payPeriod.payPeriodId
    },

    payPeriodIsOpen () {
      return this.dateConfiguration.payPeriod.status === PayPeriodStatus.Open.value
    },

    contractMode () {
      return this.isByproduct ? ContractMode.Byproducts : ContractMode.Logs
    },

    verificationType () {
      return SettlementVerificationType.LogsAccruals.value
    }
  },

  watch: {
    settlementsObject: {
      handler (val) {
        this.payees = val?.payees ?? []
      },
      deep: true,
      immediate: true
    }
  },

  created () {
    this.refreshSettlementStatuses()
  },

  methods: {
    ...mapActions('settlements', [
      'clearStaleSubModuleData',
      'setCurrentModule',
      'refreshModuleData',
      'downloadSubmoduleBlob',
      'createSettlementVerification',
      'updateSettlementVerification',
      'fetchSettlementVerifications',
      'deleteSettlementVerification'
    ]),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('export-batch', ['fetchExportBatches']),
    formatMoney,
    tonStringFromPounds,
    mbfStringFromBf,
    getDataTableDefaultItemsPerPage,

    rowClicked (item) {
      this.expanded = []
      this.$nextTick(() => {
        this.expanded = [item]
      })
    },

    getNumberOfLoads (payee) {
      let allContractLoads = []
      payee.contracts.forEach(contract => {
        allContractLoads = [...allContractLoads, ...contract.loadSummaries]
      })
      return [...new Set(allContractLoads.map(load => load.ticketNumber))].length
    },

    getPayeeTotalUnformatted (payee, isGross) {
      const ticketTotal = payee.contracts.reduce((total, c) => total + (isGross ? c.grossAmount : c.amount), 0)
      const correctionTotal = payee.corrections.reduce((prev, curr) => prev + curr.amount, 0)
      const accountPaymentTotal = payee.accountPayables?.reduce((total, payable) => total + payable.amount, 0)
      return ticketTotal + correctionTotal + accountPaymentTotal
    },

    getPayeeGrossTonsTotal ({ contracts }) {
      const value = contracts.reduce((total, c) => total + c.grossTons, 0)
      return numberWithCommas(value, TONS_DECIMALS)
    },

    getPayeeNetTonsTotal ({ contracts }) {
      const value = contracts.reduce((total, c) => total + c.netTons, 0)
      return numberWithCommas(value, TONS_DECIMALS)
    },

    getPayeeVolumeTotal ({ contracts }, volumeKey) {
      return contracts.reduce((total, c) => total + (c[volumeKey] ?? 0), 0)
    },

    getAverageRate ({ contracts }, isNet) {
      const amount = contracts.reduce((total, c) => total + c.grossAmount, 0)
      const weight = isNet
        ? contracts.reduce((total, c) => total + c.netTons, 0)
        : contracts.reduce((total, c) => total + c.grossTons, 0)

      const o = Number((amount / weight).toFixed(2))

      return (!Number.isNaN(o) && Number.isFinite(o))
        ? formatMoney(o)
        : this.$t('notAvailable')
    },

    async toggleSettlementStatus (payee) {
      if (this.dateConfiguration?.payPeriod.status === PayPeriodStatus.Exported.value || this.isExportBatch) return

      const verification = this.verificationMap[payee.payeeId]

      if (!userAssignedClaim(UserClaims.ContractManager)) {
        this.setSnackError(this.$t('userDoesNotHavePermissionToVerifySettlements'))
        return
      }

      if (verification) {
        if (verification.status === SettlementVerificationStatus.Verified.value) {
          await this.deleteSettlementVerification(verification.settlementVerificationId)
        } else {
          await this.updateSettlementVerification({
            settlementVerificationId: verification.settlementVerificationId,
            status: SettlementVerificationStatus.Verified.value,
            payPeriodId: this.payPeriodId,
            accountId: payee.payeeId,
            modifiedAt: verification.modifiedAt,
            type: this.verificationType
          })
        }
      } else {
        await this.createSettlementVerification({
          payPeriodId: this.payPeriodId,
          accountId: payee.payeeId,
          type: this.verificationType
        })
      }

      this.refreshSettlementStatuses()
    },

    getSettlementStatus (payeeId) {
      if (this.dateConfiguration.payPeriod.status === PayPeriodStatus.Exported.value || this.isExportBatch) {
        return {
          text: this.$t('verified'),
          tooltipText: this.$t('verified'),
          color: 'success white--text'
        }
      }

      const verification = this.verificationMap[payeeId]

      switch (verification?.status) {
        case SettlementVerificationStatus.Verified.value:
          const date = verification.modifiedAt
            ? utcToLocalDate(verification.modifiedAt, 'L - LT')
            : utcToLocalDate(verification.createdAt, 'L - LT')
          return {
            text: this.$t('verified'),
            tooltipText: this.$t('verifiedByUserAt', { user: verification.verifiedBy, date }),
            color: 'success white--text'
          }
        case SettlementVerificationStatus.Invalidated.value:
          return {
            text: this.$t('invalidated'),
            tooltipText: this.$t(SettlementInvalidatedReason.forInt(verification.settlementInvalidatedReason).label, { date: utcToLocalDate(verification.modifiedAt, 'L - LT') }),
            color: 'warning white--text'
          }
        default:
          return {
            tooltipText: this.$t('clickToVerify'),
            text: this.$t('needsReview'),
            color: 'blue-grey lighten-2 white--text'
          }
      }
    },

    rateVerificationClicked (payee) {
      this.rateVerification = true
      this.rateVerificationPayeeId = payee.payeeId
      this.openOrUpdateDialog({ id: this.dialogId, width: '70%' })
    },

    closeRateVerification () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.rateVerification = false
      this.rateVerificationPayeeId = undefined
    },

    closeDialogs () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.rateVerification = false
      this.rateVerificationPayeeId = undefined
      this.currentPayee = null
    },

    async refreshSettlement () {
      this.clearStaleSubModuleData({ isByproduct: this.isByproduct })
      await this.refreshModuleData(false)
    },

    async refreshSettlementStatuses () {
      const verifications = await this.fetchSettlementVerifications(this.verificationType)

      this.verificationMap = verifications.reduce((acc, v) => {
        acc[v.accountId] = v
        return acc
      }, {})
    }
  }
}
</script>
